import React, { memo } from "react"
import { Link } from "react-router-dom"
import { useDraggable } from "@dnd-kit/core"
import { Tooltip } from "react-tooltip"
import { parseISO, format as formatDate } from "date-fns"

import { calculateRowSpan, isGroupDraggable } from "./utils"
import GroupedJobServiceTooltip from "./GroupedJobServiceTooltip"

// Default period duration in minutes (1 hour)
const DEFAULT_PERIOD_DURATION_MINUTES = 60

/**
 * Get the reason why a job service is not draggable
 * @param {Object} jobService - The job service object
 * @returns {string} Human-readable reason
 */
function getNonDraggableReason(jobService) {
  if (!jobService) return "Cannot be rescheduled"

  const isCheckedIn = jobService.checked_in_at || jobService.checkedInAt
  if (isCheckedIn) {
    return "Cannot be rescheduled - work has already started (checked in)"
  }

  if (jobService.hasMultipleAssignments) {
    return "Cannot be rescheduled - has multiple surveyors assigned"
  }

  if (jobService.hasMultipleBookingDates) {
    return "Cannot be rescheduled - has multiple booking dates"
  }

  return "Cannot be rescheduled"
}

/**
 * JobServiceGroup component displays a group of job services that share
 * the same address and booking time. It shows a consolidated view with
 * a tooltip containing all individual services sorted by booking date.
 *
 * @param {Object} props
 * @param {Object} props.group - The grouped job service object from groupJobServicesByAddressAndTime
 * @param {string} props.group.groupKey - Unique key for the group
 * @param {Object} props.group.primaryService - The first/primary job service in the group
 * @param {Array} props.group.services - All job services in the group
 * @param {number} props.group.totalValue - Sum of all service prices
 * @param {number} props.group.maxEstimatedHours - Maximum estimated hours among services
 * @param {boolean} props.group.isGroup - Whether this contains multiple services
 * @param {boolean} props.group.hasNonDraggable - Whether any service is non-draggable
 * @param {number} props.periodDurationMinutes - Duration of each time period in minutes
 * @param {Function} props.onClick - Optional callback when item is clicked
 * @param {number} props.surveyorId - The surveyor ID this group belongs to
 * @param {number} props.dayIndex - The day index (0-5 for Mon-Sat)
 */
function JobServiceGroup({
  group,
  periodDurationMinutes = DEFAULT_PERIOD_DURATION_MINUTES,
  onClick,
  surveyorId,
  dayIndex
}) {
  // Determine if the group is draggable based on business rules
  const isDraggable = isGroupDraggable(group)

  // Set up draggable hook with proper ID generation
  // Use individual job service ID when not actually grouped to prevent stale group behavior
  let dragId = "job-service-group-empty"
  if (group) {
    if (group.isGroup && group.services?.length > 1) {
      dragId = `job-service-group-${group.groupKey}`
    } else {
      dragId = `job-service-${group.primaryService?.id}`
    }
  }

  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging
  } = useDraggable({
    id: dragId,
    data: {
      jobService: group?.primaryService,
      surveyorId,
      dayIndex,
      isGrouped: group?.isGroup,
      groupedServices: group?.services || []
    },
    disabled: !group || !isDraggable
  })

  if (!group || !group.primaryService) {
    return null
  }

  const {
    primaryService, services, totalValue, maxEstimatedHours, isGroup
  } = group

  // Extract primary service properties
  const { id } = primaryService
  const jobId = primaryService.job_id || primaryService.jobId
  const address = primaryService.address || "No Address"
  const bookingTime = primaryService.booking_time || primaryService.bookingTime

  // Use totalCost instead of price to match the details page
  const displayPrice = isGroup && services.length > 0
    ? services.reduce((sum, s) => sum + (parseFloat(s.total_cost || s.totalCost || s.price) || 0), 0)
    : parseFloat(primaryService.total_cost || primaryService.totalCost || primaryService.price) || 0

  // Get the earliest booking time for grouped services
  const displayBookingTime = isGroup && services.length > 0
    ? services.reduce((earliest, s) => {
      const serviceTime = s.booking_time || s.bookingTime
      if (!earliest) return serviceTime
      if (!serviceTime) return earliest

      try {
        // Parse both times to compare
        const earliestDate = parseISO(earliest)
        const serviceDate = parseISO(serviceTime)
        return serviceDate < earliestDate ? serviceTime : earliest
      } catch {
        return earliest
      }
    }, null)
    : bookingTime

  // Format booking time for display in user's local timezone
  const formatBookingTime = timeStr => {
    if (!timeStr) return ""

    try {
      // If it's already in HH:MM format, return as is
      if (/^\d{1,2}:\d{2}$/.test(timeStr)) {
        return timeStr
      }

      // Otherwise parse as ISO timestamp and convert to local time
      const date = parseISO(timeStr)
      return formatDate(date, "h:mm a")
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error formatting booking time:", error, timeStr)
      return timeStr
    }
  }

  const formattedTime = formatBookingTime(displayBookingTime)

  // With DragOverlay, the original item should NOT move — the overlay handles the ghost.
  // We just fade the source card so the user sees a "picked up from here" placeholder.
  const getDragCursor = () => {
    if (isDragging) return "grabbing"
    if (isDraggable) return "grab"
    return "pointer"
  }

  // No CSS transform on the original — DragOverlay handles the moving ghost.
  // Only apply a visual "picked-up" state (faded placeholder).
  const dragStyle = isDragging ? { opacity: 0.4 } : {}

  // Calculate row span based on max estimated hours
  const rowSpan = calculateRowSpan(maxEstimatedHours, periodDurationMinutes)

  // Truncate address at Australian state/territory abbreviation if present,
  // otherwise fall back to the character-limit truncation
  const AU_STATES = /\b(NSW|VIC|QLD|SA|WA|TAS|NT|ACT)\b/
  const stateMatch = AU_STATES.exec(address)
  const displayAddress = stateMatch
    ? address.substring(0, stateMatch.index).replace(/,\s*$/, "").trim()
    : address.length > 35
      ? `${address.substring(0, 35)}...`
      : address

  const itemStyle = {
    height: rowSpan > 1 ? `${rowSpan * 100}%` : undefined
  }

  // Main container style without cursor (cursor handled by individual elements)
  const containerStyle = {
    ...itemStyle,
    ...dragStyle
  }

  // Sort services by booking date for tooltip display
  const sortedServices = [...services].sort((a, b) => {
    const dateA = a.booking_date || a.bookingDate || ""
    const dateB = b.booking_date || b.bookingDate || ""
    return dateA.localeCompare(dateB)
  })

  // Generate unique tooltip ID for this group
  const tooltipId = `job-service-group-tooltip-${group.groupKey || id}`

  // Handle click event for non-grouped items
  const handleClick = e => {
    if (isDragging) {
      e.preventDefault()
      return
    }
    if (onClick) {
      onClick(primaryService, e)
    }
  }

  // Handle keyboard navigation
  const handleKeyDown = e => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      handleClick(e)
    }
  }

  // Build the job details link (for non-draggable items)
  const jobDetailsLink = !isDraggable && jobId && id
    ? `/jobs/${jobId}/job_services/${id}`
    : null

  // Get estimated hours for display
  const getEstimatedHours = () => {
    if (isGroup && services.length > 0) {
      // For grouped services, show the maximum estimated hours
      return maxEstimatedHours || 0
    }
    // For single service, get estimated hours
    return parseFloat(primaryService.estimated_hours || primaryService.estimatedHours) || 0
  }

  const estimatedHours = getEstimatedHours()

  // Render the item content
  const renderContent = () => (
    <div className="job-item-content">
      {/* Left sidebar: drag handle or lock icon */}
      <div className="job-item-sidebar">
        {isDraggable ? (
          <div
            className="drag-handle"
            title="Drag to reschedule"
            style={{ cursor: getDragCursor() }}
            {...listeners}
            {...attributes}>
            <span className="drag-icon">⋮⋮</span>
          </div>
        ) : (
          <div className="non-draggable-indicator" title={getNonDraggableReason(primaryService)}>
            <i className="bi bi-lock-fill" aria-label="Locked" />
          </div>
        )}
      </div>

      {/* Right main content */}
      <div className="job-item-main">
        {/* Top row: job number + detail link */}
        <div className="job-header">
          {primaryService.job_no && (
            <div className="job-no">
              {primaryService.job_no}
              {primaryService.has_history && (
                <span
                  className="ml-1"
                  title="This address has job history"
                  style={{ color: "#007bff", fontSize: "0.85em" }}>
                  ⟲
                </span>
              )}
            </div>
          )}

          {/* Detail link icon — top-right of the main content area */}
          {!isGroup && isDraggable && jobId && id && (
            <div
              className="detail-link-indicator"
              title="View job service details"
              onClick={e => {
                e.stopPropagation()
                const link = `/jobs/${jobId}/job_services/${id}`
                window.open(link, "_blank", "noopener,noreferrer")
              }}
              role="button"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  e.stopPropagation()
                  const link = `/jobs/${jobId}/job_services/${id}`
                  window.open(link, "_blank", "noopener,noreferrer")
                }
              }}>
              <i className="bi bi-box-arrow-up-right detail-icon" aria-label="View details" />
            </div>
          )}
        </div>

        {/* Middle content section */}
        <div className="job-body">
          <div className="job-address" title={address}>
            {displayAddress}
          </div>

          <div className="job-price">
            $
            {displayPrice.toFixed(0)}
          </div>

          {isGroup && services.length > 1 && (
            <div
              className="grouped-indicator"
              data-tooltip-id={tooltipId}
              title="Hover to see all job services">
              +
              {services.length - 1}
              {" "}
              more
            </div>
          )}
        </div>

        {/* Footer: time + estimated hours */}
        <div className="job-footer">
          <div className="job-time">
            {formattedTime ? (
              <>
                <i className="bi bi-clock" />
                {" "}
                {formattedTime}
              </>
            ) : (
              <span style={{ opacity: 0.5 }}>No time</span>
            )}
          </div>
          {estimatedHours > 0 && (
            <div className="job-estimated-hours">
              {estimatedHours}
              h
            </div>
          )}
        </div>
      </div>
    </div>
  )

  // Build class names
  const itemClasses = [
    "schedule-job-item",
    isGroup ? "grouped" : "",
    jobDetailsLink ? "clickable" : "",
    isDragging ? "dragging" : "",
    !isDraggable ? "non-draggable" : ""
  ].filter(Boolean).join(" ")

  // Render with tooltip for grouped items
  // Hide tooltip during drag operations
  if (isGroup && services.length > 1 && !isDragging) {
    return (
      <>
        <div
          ref={setNodeRef}
          className={itemClasses}
          style={containerStyle}>
          {jobDetailsLink && !isDragging ? (
            <Link
              to={jobDetailsLink}
              className="job-item-link"
              onClick={e => {
                e.preventDefault()
                window.open(jobDetailsLink, "_blank", "noopener,noreferrer")
              }}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  window.open(jobDetailsLink, "_blank", "noopener,noreferrer")
                }
              }}>
              {renderContent()}
            </Link>
          ) : (
            <div
              onClick={handleClick}
              onKeyDown={handleKeyDown}
              role="button"
              tabIndex={0}>
              {renderContent()}
            </div>
          )}
        </div>
        <Tooltip
          id={tooltipId}
          place="right"
          className="grouped-job-tooltip"
          events={["click"]}
          clickable
          style={{
            backgroundColor: "#fff",
            color: "#333",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            borderRadius: "8px",
            padding: "0",
            maxWidth: "400px",
            zIndex: 9999
          }}>
          <GroupedJobServiceTooltip
            services={sortedServices}
            totalValue={totalValue} />
        </Tooltip>
      </>
    )
  }

  // Render single item without tooltip (or grouped item being dragged)
  return (
    <div
      ref={setNodeRef}
      className={itemClasses}
      style={containerStyle}>
      {jobDetailsLink && !isDragging ? (
        <Link
          to={jobDetailsLink}
          className="job-item-link"
          onClick={e => {
            e.preventDefault()
            window.open(jobDetailsLink, "_blank", "noopener,noreferrer")
          }}
          onKeyDown={e => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault()
              window.open(jobDetailsLink, "_blank", "noopener,noreferrer")
            }
          }}>
          {renderContent()}
        </Link>
      ) : (
        <div
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          role="button"
          tabIndex={0}>
          {renderContent()}
        </div>
      )}
    </div>
  )
}

// Memoize so individual job cards don’t re-render during drag pointer-move events.
// Props (group, surveyorId, dayIndex) only change when schedule data changes, not on drag moves.
export default memo(JobServiceGroup)
